/**
 * The little animated scenes on the How to play pages: where each sprite is at time `t`. A stage
 * is 15 x 5 cells of 16 pixels; everything loops. Pure.
 */

import type { SpriteName } from '../../render/sprites';

export interface Actor {
  readonly sprite: SpriteName;
  readonly frame: number;
  /** Stage pixels. */
  readonly x: number;
  readonly y: number;
}

export interface Label {
  readonly text: string;
  /** Centre x (or left, with `left`), top y, stage pixels. */
  readonly x: number;
  readonly y: number;
  readonly colour: number;
  readonly left?: boolean;
}

export interface Stage {
  readonly actors: readonly Actor[];
  readonly labels: readonly Label[];
}

export const STAGE_COLS = 15;
export const STAGE_ROWS = 5;
const C = 16;

/** 0..1 through [t0, t1]. */
export function seg(t: number, t0: number, t1: number): number {
  return t <= t0 ? 0 : t >= t1 ? 1 : (t - t0) / (t1 - t0);
}

function at(sprite: SpriteName, col: number, row: number, frame = 0): Actor {
  return { sprite, frame, x: Math.round(col * C), y: Math.round(row * C) };
}

/** A row of one sprite from column `from` to `to`, leaving out the columns `skip` names. */
function row(sprite: SpriteName, r: number, from: number, to: number, skip = (_c: number): boolean => false): Actor[] {
  const out: Actor[] = [];
  for (let c = from; c <= to; c++) if (!skip(c)) out.push(at(sprite, c, r));
  return out;
}

/** Digby running across, digging, collecting three diamonds, into the opening exit. */
export function goalStage(t: number, anim: number): Stage {
  const k = t % 5200;
  const x = 1 + 12 * seg(k, 400, 3600);
  const gems = [4, 7, 10];
  const got = gems.filter((c) => x >= c).length;
  const open = got === gems.length;
  const actors: Actor[] = [
    ...row('steel', 0, 0, 3), ...row('steel', 0, 11, 14),
    ...row('dirt', 1, 0, 14), ...row('dirt', 3, 0, 14), ...row('steel', 4, 0, 14),
  ];
  actors.push(...row('dirt', 2, 0, 14, (c) => c < x + 1 || gems.includes(c) || c === 13));
  for (const c of gems) if (x < c) actors.push(at('diamond', c, 2, (anim >> 1) + c));
  actors.push(at(open && (anim >> 3) % 2 === 0 ? 'door' : 'steel', 13, 2));
  if (k < 3650) actors.push(at(k > 400 && k < 3600 ? 'digbyRight' : 'digbyStand', x, 2, anim >> 1));
  const quota = { text: open ? 'EXIT OPEN!' : `◆ ${got} OF 3`, x: 7.5 * C, y: 4, colour: open ? 7 : 1 };
  return { actors, labels: [quota] };
}

/** The cast, standing still to be looked at. */
export function castStage(t: number, anim: number): Stage {
  const mood = Math.floor(t / 1500) % 3;
  const blink = anim % 16 < 3 ? 'digbyBlink' : 'digbyStand';
  const tap = anim % 6 < 3 ? 'digbyTap' : 'digbyStand';
  const digby: SpriteName = mood === 1 ? blink : mood === 2 ? tap : 'digbyStand';
  return {
    actors: [
      at(digby, 1.5, 1.5), at('dirt', 5, 1.5), at('diamond', 8.5, 1.5, anim >> 1),
      at((anim >> 3) % 2 === 0 ? 'door' : 'steel', 12, 1.5),
    ],
    labels: [
      { text: 'DIGBY', x: 2 * C, y: 3 * C, colour: 1 },
      { text: 'DIRT', x: 5.5 * C, y: 3 * C, colour: 1 },
      { text: 'DIAMOND', x: 9 * C, y: 3 * C, colour: 1 },
      { text: 'EXIT', x: 12.5 * C, y: 3 * C, colour: 1 },
    ],
  };
}

/** A boulder falls and rolls off another; Digby pushes one along. */
export function boulderStage(t: number, anim: number): Stage {
  const k = t % 4400;
  const actors: Actor[] = [...row('dirt', 3, 0, 14), ...row('steel', 4, 0, 14), at('boulder', 2, 2)];
  // Falling boulder: down one cell, roll right off the round one below, drop to the floor.
  const y1 = seg(k, 300, 600);
  const roll = seg(k, 700, 950);
  const y2 = seg(k, 950, 1200);
  actors.push(at('boulder', 2 + roll, y1 + y2));
  // Pushing: Digby strains, then boulder and Digby move right together.
  const push = seg(k, 1600, 3800);
  const step = Math.floor(push * 3) / 3 + seg((push * 3) % 1, 0.6, 1) / 3;
  actors.push(at('boulder', 9 + step * 3, 2));
  actors.push(at(push > 0 && push < 1 ? 'digbyRight' : 'digbyStand', 8 + step * 3, 2, anim >> 1));
  return { actors, labels: [] };
}

/** A firefly patrols; a boulder drops on a butterfly and it bursts into diamonds. */
export function creatureStage(t: number, anim: number): Stage {
  const k = t % 4600;
  const loop = [[1, 1], [2, 1], [3, 1], [4, 1], [4, 2], [3, 2], [2, 2], [1, 2]] as const;
  const p = loop[Math.floor(k / 260) % loop.length] ?? [1, 1];
  const actors: Actor[] = [
    ...row('steel', 0, 0, 5), ...row('steel', 3, 0, 5),
    at('steel', 0, 1), at('steel', 0, 2), at('steel', 5, 1), at('steel', 5, 2),
    ...row('dirt', 4, 8, 12),
  ];
  actors.push(at('firefly', p[0], p[1], anim >> 1));
  const drop = seg(k, 900, 1250);
  if (k < 1300) {
    actors.push(at('boulder', 10, drop));
    actors.push(at('butterfly', 10, 2, anim >> 1));
  } else if (k < 1700) {
    const stage = Math.floor((k - 1300) / 80);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) actors.push(at('explosion', 10 + dx, 2 + dy, stage));
    }
  } else {
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) actors.push(at('diamond', 10 + dx, 2 + dy, (anim >> 1) + dx * 3 + dy));
    }
  }
  return { actors, labels: [] };
}
